import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge,
  Spinner, Alert
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTrainingAuth } from '../../context/TrainingAuthContext';
import TrainingNavbar from '../../components/TrainingNavbar';
import trainingService from '../../services/trainingService';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

const STATUS_CONFIG = {
  draft:     { label: 'Draft',             bg: 'secondary' },
  submitted: { label: 'Pending Review',    bg: 'primary'   },
  sent_back: { label: 'Corrections Needed', bg: 'danger'   },
  approved:  { label: 'Approved',          bg: 'success'   },
};

const ROLE_LABEL = {
  training_coordinator: 'Training Coordinator',
  training_document_controller: 'Training Document Controller',
};

const TrainingCoordinatorDashboard = () => {
  const { trainingUser } = useTrainingAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { page, setPage, totalPages, paginatedItems: paginatedSubmissions } = usePagination(submissions);

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await trainingService.listSubmissions();
      if (data.success) setSubmissions(data.submissions);
      else setError(data.message || 'Failed to load submissions');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  const counts = {
    total: submissions.length,
    draft: submissions.filter(s => s.current_status === 'draft').length,
    pending: submissions.filter(s => s.current_status === 'submitted').length,
    sentBack: submissions.filter(s => s.current_status === 'sent_back').length,
    approved: submissions.filter(s => s.current_status === 'approved').length,
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-GB');
  };

  return (
    <>
      <TrainingNavbar />
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="mb-0">
              <i className="fas fa-graduation-cap text-success me-2"></i>
              Training Dashboard
            </h2>
            <p className="text-muted mb-0">
              {ROLE_LABEL[trainingUser?.role] || trainingUser?.role} — {trainingUser?.full_name}
            </p>
          </Col>
          <Col xs="auto">
            <Button variant="success" onClick={() => navigate('/training/new')}>
              <i className="fas fa-plus me-2"></i>New Certificate
            </Button>
          </Col>
        </Row>

        {/* Stats */}
        <Row className="mb-4 g-3">
          {[
            { label: 'Total', count: counts.total, color: 'primary' },
            { label: 'Drafts', count: counts.draft, color: 'secondary' },
            { label: 'Pending Review', count: counts.pending, color: 'info' },
            { label: 'Corrections Needed', count: counts.sentBack, color: 'danger' },
            { label: 'Approved', count: counts.approved, color: 'success' },
          ].map(({ label, count, color }) => (
            <Col key={label} xs={6} md={4} lg={2}>
              <Card className="border-0 shadow-sm text-center h-100">
                <Card.Body>
                  <h3 className={`text-${color} mb-1`}>{count}</h3>
                  <small className="text-muted">{label}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        {/* Sent-back alert */}
        {counts.sentBack > 0 && (
          <Alert variant="warning" className="d-flex align-items-center gap-2">
            <i className="fas fa-exclamation-triangle fa-lg"></i>
            <span>
              <strong>{counts.sentBack} submission{counts.sentBack > 1 ? 's' : ''} need{counts.sentBack === 1 ? 's' : ''} corrections.</strong>{' '}
              Please review the reviewer's comments and resubmit.
            </span>
          </Alert>
        )}

        {/* Table */}
        <Card className="shadow-sm">
          <Card.Header className="bg-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">My Submissions</h5>
            <Button variant="outline-secondary" size="sm" onClick={loadSubmissions}>
              <i className="fas fa-sync me-1"></i>Refresh
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="success" />
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-file-alt fa-3x mb-3 d-block opacity-25"></i>
                No submissions yet. Click "New Certificate" to get started.
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Course</th>
                      <th>Trainee</th>
                      <th>Cert No</th>
                      <th>Issue Date</th>
                      <th>Status</th>
                      <th>Reviewer Comments</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSubmissions.map(sub => {
                      const statusCfg = STATUS_CONFIG[sub.current_status] || { label: sub.current_status || 'Unknown', bg: 'secondary' };
                      return (
                        <tr key={sub.id}
                          className={sub.current_status === 'sent_back' ? 'table-danger' : ''}>
                          <td>
                            <div className="fw-semibold">{sub.courseName}</div>
                            <small className="text-muted">{sub.courseCode}</small>
                          </td>
                          <td>{sub.traineeName}</td>
                          <td><small className="text-muted">{sub.certNo || '—'}</small></td>
                          <td><small>{formatDate(sub.issueDate)}</small></td>
                          <td>
                            <Badge bg={statusCfg.bg}>{statusCfg.label}</Badge>
                            {sub.hasPdf && (
                              <Badge bg="light" text="dark" className="ms-1" title="PDF ready">
                                <i className="fas fa-file-pdf text-success"></i>
                              </Badge>
                            )}
                          </td>
                          <td>
                            {sub.current_status === 'sent_back' && sub.review_comments ? (
                              <small className="text-danger">
                                <i className="fas fa-comment-alt me-1"></i>
                                {sub.review_comments.length > 60
                                  ? sub.review_comments.slice(0, 60) + '…'
                                  : sub.review_comments}
                              </small>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => navigate(`/training/submissions/${sub.id}`)}
                              >
                                <i className="fas fa-eye me-1"></i>View
                              </Button>
                              {(sub.current_status === 'draft' || sub.current_status === 'sent_back') && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => navigate(`/training/submissions/${sub.id}/edit`)}
                                >
                                  <i className="fas fa-edit me-1"></i>Edit
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <TablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default TrainingCoordinatorDashboard;
